/**
 * Gemstone configurator (theme asset).
 *
 * Renders entirely from config inlined into the page by the theme (shop
 * configurator metafield, populated by the backend catalog sync) —
 * no storefront call to our backend is made to render the widget. Adds a
 * multi-line cart bundle via Shopify's native /cart/add.js: gemstone + optional
 * setting variant + optional cert variant, linked by the _gj_bundle_id line
 * item property (which the order webhook reads to map orders back).
 */
(function () {
  'use strict';

  const FORM_GEMSTONE = 'Gemstone';
  const FORM_RING = 'Ring';
  const FORM_PENDANT = 'Pendant';

  function gidToNumericId(gid) {
    if (gid == null || gid === '') return null;
    const s = String(gid);
    if (/^\d+$/.test(s)) return Number(s);
    const parts = s.split('/');
    const last = parts[parts.length - 1];
    return last && /^\d+$/.test(last) ? Number(last) : null;
  }

  function formatMoney(amount, currencySymbol) {
    const sym = currencySymbol || '₹';
    const n = Number(amount) || 0;
    return sym + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }

  function parseConfig(raw) {
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  /**
   * Read the configurator config inlined into the page by the theme.
   *
   * The config (forms, metals, sizes, certificates, setting variant GIDs and
   * prices) is published to the shop's configurator metafield by the
   * backend catalog sync and rendered into the page at request time, so the
   * storefront never calls our backend to render the widget. We prefer a
   * `<script type="application/json">` block (robust for large/quoted JSON)
   * and fall back to the `data-config` attribute.
   */
  function readInlineConfig(root) {
    const scriptEl = document.getElementById('gj-gemstone-configurator-config');
    if (scriptEl && scriptEl.textContent && scriptEl.textContent.trim()) {
      const fromScript = parseConfig(scriptEl.textContent.trim());
      if (fromScript) return fromScript;
    }
    return parseConfig(root.dataset.config);
  }

  class GemstoneConfigurator {
    constructor(root) {
      this.root = root;
      this.gemstoneVariantId = gidToNumericId(root.dataset.variantId);
      this.gemstonePrice = Number(root.dataset.price) || 0;
      this.config = readInlineConfig(root);
      this.currencySymbol = '₹';

      this.state = {
        form: FORM_GEMSTONE,
        metal: null,
        sizeType: null,
        ringSize: null,
        certificate: null,
      };

      this.messageEl = null;
      this.priceEl = null;
      this.breakdownEl = null;
      this.submitBtn = null;
    }

    init() {
      if (!this.gemstoneVariantId) {
        this.root.innerHTML = '<p class="gj-configurator__message is-error">Product variant not found.</p>';
        return;
      }

      if (!this.config) {
        // Config is inlined by the theme from the shop metafield (populated by
        // the backend catalog sync). If it is missing the store likely needs a
        // re-sync; we never fall back to a network call here by design.
        if (window.console && console.warn) {
          console.warn('[configurator] Config not found on page.');
        }
        this.root.innerHTML =
          '<p class="gj-configurator__message is-error">Configurator options could not be loaded.</p>';
        return;
      }

      this.applyDefaults();
      this.render();
      this.bindEvents();
      this.updatePrice();
    }

    applyDefaults() {
      const forms = this.config.forms || [];
      this.state.form = forms.includes(FORM_GEMSTONE) ? FORM_GEMSTONE : forms[0] || FORM_GEMSTONE;

      const metals = this.config.metals || [];
      this.state.metal = metals[0] || null;

      const sizeTypes = this.config.size_types || [];
      this.state.sizeType = sizeTypes[0] || 'India';

      // No default ring size: the buyer has to pick one, so nobody gets size 1 by accident.
      this.state.ringSize = null;

      const certs = this.config.certificates || [];
      const defaultCert = certs.find((c) => c.is_default) || certs[0];
      this.state.certificate = defaultCert || null;
    }

    filteredRingSizes() {
      const all = this.config.ring_sizes || [];
      if (!this.state.sizeType) return all;
      return all.filter((rs) => rs.size_type === this.state.sizeType);
    }

    settingForForm() {
      const settings = this.config.settings || {};
      return settings[this.state.form] || null;
    }

    settingVariantId() {
      const setting = this.settingForForm();
      if (!setting || !this.state.metal) return null;
      const map = setting.variants_by_metal || {};
      return gidToNumericId(map[this.state.metal]);
    }

    certVariantId() {
      const cert = this.state.certificate;
      if (!cert || !cert.price || cert.price <= 0) return null;
      return gidToNumericId(cert.variant_id);
    }

    metalPrice() {
      if (this.state.form === FORM_GEMSTONE) return 0;
      const setting = this.settingForForm();
      if (!setting || !this.state.metal) return 0;
      const prices = setting.metal_prices || {};
      return Number(prices[this.state.metal]) || 0;
    }

    sizeExtraPrice() {
      if (this.state.form !== FORM_RING || !this.state.ringSize) return 0;
      const sizes = this.config.ring_sizes || [];
      const match = sizes.find(
        (rs) => rs.size_type === this.state.sizeType && String(rs.size) === String(this.state.ringSize)
      );
      return match ? Number(match.extra_price) || 0 : 0;
    }

    certPrice() {
      const cert = this.state.certificate;
      return cert ? Number(cert.price) || 0 : 0;
    }

    totalPrice() {
      return this.gemstonePrice + this.metalPrice() + this.sizeExtraPrice() + this.certPrice();
    }

    canAddToCart() {
      if (this.state.form === FORM_RING && (!this.state.sizeType || !this.state.ringSize)) {
        return false;
      }
      if (this.state.form !== FORM_GEMSTONE) {
        const setting = this.settingForForm();
        if (!setting || !this.state.metal) return false;
        const prices = setting.metal_prices || {};
        if (prices[this.state.metal] == null) return false;
        if (!this.settingVariantId()) return false;
      }
      if (this.certPrice() > 0 && !this.certVariantId()) return false;
      return true;
    }

    render() {
      const forms = this.config.forms || [];
      const metals = this.config.metals || [];
      const sizeTypes = this.config.size_types || [];
      const certs = this.config.certificates || [];

      this.root.innerHTML = `
        <div class="gj-configurator">
          <h3 class="gj-configurator__title">Configure your purchase</h3>

          <div class="gj-configurator__section" data-section="form">
            <span class="gj-configurator__label">Product type</span>
            <div class="gj-configurator__choices" role="radiogroup" aria-label="Product type">
              ${forms
                .map(
                  (f) =>
                    `<button type="button" class="gj-configurator__choice${f === this.state.form ? ' is-selected' : ''}" data-form="${esc(f)}" aria-pressed="${f === this.state.form}">${esc(f)}</button>`
                )
                .join('')}
            </div>
          </div>

          <div class="gj-configurator__section${this.state.form === FORM_GEMSTONE ? ' gj-configurator__hidden' : ''}" data-section="metal">
            <span class="gj-configurator__label">Metal</span>
            <div class="gj-configurator__choices" role="radiogroup" aria-label="Metal">
              ${metals
                .map(
                  (m) =>
                    `<button type="button" class="gj-configurator__choice${m === this.state.metal ? ' is-selected' : ''}" data-metal="${esc(m)}" aria-pressed="${m === this.state.metal}">${esc(m)}</button>`
                )
                .join('')}
            </div>
          </div>

          <div class="gj-configurator__row${this.state.form !== FORM_RING ? ' gj-configurator__hidden' : ''}" data-section="size">
            <div class="gj-configurator__section">
              <label class="gj-configurator__label" for="gj-size-type">Size type</label>
              <select id="gj-size-type" class="gj-configurator__select">
                ${sizeTypes.map((st) => `<option value="${esc(st)}"${st === this.state.sizeType ? ' selected' : ''}>${esc(st)}</option>`).join('')}
              </select>
            </div>
            <div class="gj-configurator__section">
              <label class="gj-configurator__label" for="gj-ring-size">Ring size</label>
              <select id="gj-ring-size" class="gj-configurator__select">
                <option value=""${this.state.ringSize ? '' : ' selected'}>Select size</option>
                ${this.filteredRingSizes()
                  .map(
                    (rs) =>
                      `<option value="${esc(rs.size)}"${String(rs.size) === String(this.state.ringSize) ? ' selected' : ''}>${esc(rs.size)}</option>`
                  )
                  .join('')}
              </select>
            </div>
          </div>

          <div class="gj-configurator__section" data-section="cert">
            <label class="gj-configurator__label" for="gj-certificate">Certificate</label>
            <select id="gj-certificate" class="gj-configurator__select">
              ${certs
                .map((c) => {
                  // Names like "Government Approved Lab (FREE)" already say it is free.
                  const freeInName = /\bfree\b/i.test(c.name || '');
                  const priceLabel = c.price > 0 ? ` (+${formatMoney(c.price, this.currencySymbol)})` : freeInName ? '' : ' (Free)';
                  const selected = this.state.certificate && c.id === this.state.certificate.id;
                  return `<option value="${esc(c.id)}"${selected ? ' selected' : ''}>${esc(c.name)}${priceLabel}</option>`;
                })
                .join('')}
            </select>
          </div>

          <div class="gj-configurator__price" data-price></div>
          <div class="gj-configurator__price-breakdown" data-breakdown></div>

          <button type="button" class="gj-configurator__submit" data-submit>Add to cart</button>
          <div class="gj-configurator__message" data-message></div>
        </div>
      `;

      this.priceEl = this.root.querySelector('[data-price]');
      this.breakdownEl = this.root.querySelector('[data-breakdown]');
      this.submitBtn = this.root.querySelector('[data-submit]');
      this.messageEl = this.root.querySelector('[data-message]');
    }

    bindEvents() {
      this.root.addEventListener('click', (e) => {
        const formBtn = e.target.closest('[data-form]');
        if (formBtn) {
          this.state.form = formBtn.dataset.form;
          this.syncSectionVisibility();
          this.refreshChoiceStates();
          this.updatePrice();
          return;
        }

        const metalBtn = e.target.closest('[data-metal]');
        if (metalBtn) {
          this.state.metal = metalBtn.dataset.metal;
          this.refreshChoiceStates();
          this.updatePrice();
        }
      });

      const sizeTypeEl = this.root.querySelector('#gj-size-type');
      if (sizeTypeEl) {
        sizeTypeEl.addEventListener('change', () => {
          this.state.sizeType = sizeTypeEl.value;
          this.state.ringSize = null;
          this.refreshRingSizeOptions();
          this.updatePrice();
        });
      }

      const ringSizeEl = this.root.querySelector('#gj-ring-size');
      if (ringSizeEl) {
        ringSizeEl.addEventListener('change', () => {
          this.state.ringSize = ringSizeEl.value || null;
          this.updatePrice();
        });
      }

      const certEl = this.root.querySelector('#gj-certificate');
      if (certEl) {
        certEl.addEventListener('change', () => {
          const certs = this.config.certificates || [];
          this.state.certificate = certs.find((c) => c.id === certEl.value) || null;
          this.updatePrice();
        });
      }

      if (this.submitBtn) {
        this.submitBtn.addEventListener('click', () => this.addToCart());
      }
    }

    syncSectionVisibility() {
      const metalSection = this.root.querySelector('[data-section="metal"]');
      const sizeSection = this.root.querySelector('[data-section="size"]');
      if (metalSection) {
        metalSection.classList.toggle('gj-configurator__hidden', this.state.form === FORM_GEMSTONE);
      }
      if (sizeSection) {
        sizeSection.classList.toggle('gj-configurator__hidden', this.state.form !== FORM_RING);
      }
    }

    refreshChoiceStates() {
      this.root.querySelectorAll('[data-form]').forEach((btn) => {
        btn.classList.toggle('is-selected', btn.dataset.form === this.state.form);
        btn.setAttribute('aria-pressed', String(btn.dataset.form === this.state.form));
      });
      this.root.querySelectorAll('[data-metal]').forEach((btn) => {
        btn.classList.toggle('is-selected', btn.dataset.metal === this.state.metal);
        btn.setAttribute('aria-pressed', String(btn.dataset.metal === this.state.metal));
      });
    }

    refreshRingSizeOptions() {
      const ringSizeEl = this.root.querySelector('#gj-ring-size');
      if (!ringSizeEl) return;
      const sizes = this.filteredRingSizes();
      ringSizeEl.innerHTML = `<option value=""${this.state.ringSize ? '' : ' selected'}>Select size</option>` + sizes
        .map(
          (rs) =>
            `<option value="${esc(rs.size)}"${String(rs.size) === String(this.state.ringSize) ? ' selected' : ''}>${esc(rs.size)}</option>`
        )
        .join('');
    }

    updatePrice() {
      const total = this.totalPrice();
      const parts = [`Gemstone ${formatMoney(this.gemstonePrice, this.currencySymbol)}`];
      const metal = this.metalPrice();
      if (metal > 0) parts.push(`Setting ${formatMoney(metal, this.currencySymbol)}`);
      const sizeExtra = this.sizeExtraPrice();
      if (sizeExtra > 0) parts.push(`Size adj. ${formatMoney(sizeExtra, this.currencySymbol)}`);
      const cert = this.certPrice();
      if (cert > 0) parts.push(`Certificate ${formatMoney(cert, this.currencySymbol)}`);

      if (this.priceEl) {
        this.priceEl.textContent = formatMoney(total, this.currencySymbol);
      }
      if (this.breakdownEl) {
        this.breakdownEl.textContent = parts.join(' · ');
      }

      if (this.submitBtn) {
        const ok = this.canAddToCart();
        this.submitBtn.disabled = !ok;
        if (!ok && this.state.form === FORM_RING && (!this.state.sizeType || !this.state.ringSize)) {
          this.showMessage('Please choose a ring size.', 'hint');
        } else if (!ok && this.state.form !== FORM_GEMSTONE && this.settingForForm() && !this.state.metal) {
          this.showMessage('Please choose a metal.', 'hint');
        } else if (!ok && this.state.form !== FORM_GEMSTONE) {
          this.showMessage("Ring and pendant options couldn't load. Please refresh the page.", 'error');
        } else {
          this.clearMessage();
        }
      }
    }

    showMessage(text, type) {
      if (!this.messageEl) return;
      this.messageEl.textContent = text;
      this.messageEl.className = `gj-configurator__message is-${type}`;
    }

    clearMessage() {
      if (!this.messageEl) return;
      this.messageEl.textContent = '';
      this.messageEl.className = 'gj-configurator__message';
    }

    buildGemstoneProperties(bundleId) {
      const cert = this.state.certificate;
      const metal = this.metalPrice();
      const certAmt = this.certPrice();
      const props = {
        _gj_form: this.state.form,
        _gj_certificate: cert ? cert.name : '',
        _gj_bundle_id: bundleId,
        _gj_configured: '1',
        _gj_metal_amount: String(metal),
        _gj_cert_amount: String(certAmt),
        _gj_total_amount: String(this.totalPrice()),
      };
      if (this.state.form !== FORM_GEMSTONE && this.state.metal) {
        props._gj_metal = this.state.metal;
      }
      if (this.state.form === FORM_RING) {
        props._gj_size_type = this.state.sizeType || '';
        props._gj_size = this.state.ringSize || '';
      }
      return props;
    }

    buildComponentProperties(bundleId, component) {
      const props = {
        _gj_bundle_id: bundleId,
        _gj_component: component,
        _gj_configured: '1',
      };
      if (component === 'setting' && this.state.form === FORM_RING) {
        const st = this.state.sizeType || '';
        const sz = this.state.ringSize || '';
        if (st || sz) props['Ring size'] = `${st} ${sz}`.trim();
      }
      return props;
    }

    buildCartItems(bundleId) {
      const items = [
        {
          id: this.gemstoneVariantId,
          quantity: 1,
          properties: this.buildGemstoneProperties(bundleId),
        },
      ];

      if (this.state.form !== FORM_GEMSTONE) {
        const settingVariantId = this.settingVariantId();
        if (settingVariantId) {
          items.push({
            id: settingVariantId,
            quantity: 1,
            properties: this.buildComponentProperties(bundleId, 'setting'),
          });
        }
      }

      const certVariantId = this.certVariantId();
      if (certVariantId) {
        items.push({
          id: certVariantId,
          quantity: 1,
          properties: this.buildComponentProperties(bundleId, 'cert'),
        });
      }

      return items;
    }

    async addToCart() {
      if (!this.canAddToCart()) return;

      this.submitBtn.disabled = true;
      this.clearMessage();

      const bundleId = `gj-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const items = this.buildCartItems(bundleId);

      try {
        const cartUI = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const payload = { items };

        if (cartUI && typeof cartUI.getSectionsToRender === 'function') {
          payload.sections = cartUI.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
          if (typeof cartUI.setActiveElement === 'function') {
            cartUI.setActiveElement(document.activeElement);
          }
        }

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) {
          const msg = data.description || data.message || 'Could not add to cart';
          throw new Error(msg);
        }

        this.showMessage('Added to cart!', 'success');
        this.openThemeCart(cartUI, data);
        document.dispatchEvent(new CustomEvent('gj:configurator:added', { detail: { bundleId, items: data.items || data } }));
      } catch (err) {
        this.showMessage(err.message || 'Failed to add to cart', 'error');
      } finally {
        this.submitBtn.disabled = !this.canAddToCart();
      }
    }

    openThemeCart(cartUI, addResponse) {
      if (!cartUI) {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gj-configurator', cartData: addResponse });
        }
        return;
      }

      // Dawn expects cart/add response shape: { key, sections, ... } — not /cart.js.
      const parsed = { ...addResponse };
      if (!parsed.key && Array.isArray(parsed.items) && parsed.items.length) {
        parsed.key = parsed.items[0].key;
        parsed.id = parsed.items[0].variant_id;
      }

      if (typeof cartUI.renderContents === 'function' && parsed.sections) {
        try {
          cartUI.renderContents(parsed);
          return;
        } catch (e) {
          /* fall through to pub/sub refresh */
        }
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gj-configurator', cartData: parsed });
      }

      if (typeof cartUI.open === 'function' && cartUI.tagName === 'CART-DRAWER') {
        cartUI.open();
      }
    }
  }

  function boot() {
    const root = document.getElementById('gj-gemstone-configurator');
    if (!root) return;
    const widget = new GemstoneConfigurator(root);
    widget.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
